import { getCurrentInstance } from 'vue'
import type { Store } from 'vuex'

// Vuex 3 adds `$store` by augmenting a zero-parameter `interface Vue`, but Vue
// 2.7 redeclared `Vue` with five type parameters. The merge is illegal, so the
// augmentation is dropped and `$store` is absent from the instance type. This
// composable is the one place that reads it, so the shape is asserted here
// instead of patching `Vue` repo-wide for an API that Pinia replaces.
type InstanceWithStore<S> = { $store?: Store<S> }

/**
 * Returns the Vuex store from inside `setup()` / `<script setup>`.
 *
 * Vuex 3 predates the Composition API and ships no `useStore` of its own, so we
 * read the store off the current instance the same way `this.$store` does in an
 * Options API component.
 *
 * Deliberately NOT `import store from '@state/store'`: the singleton import
 * would bypass the store that tests inject via `createComponentMocks({ store })`
 * (see tests/unit/setup.js), making every store-backed component untestable.
 */
export function useStore<S = any>(): Store<S> {
    const instance = getCurrentInstance()

    if (!instance) {
        throw new Error('useStore() can only be called inside setup() or <script setup>.')
    }

    const store = (instance.proxy as unknown as InstanceWithStore<S>).$store

    if (!store) {
        throw new Error(
            'useStore() found no Vuex store on this component. In tests, mount it with createComponentMocks({ store }).'
        )
    }

    return store
}
